/**
 * The app shell for every signed-in route: the rail on the left, the center
 * (the route's surface) with the Ask dock at its foot (every page but
 * Settings, `hasAskDock`), and the sheet on the right that any surface opens
 * a record in (`useSheet`).
 *
 * The keyboard is the shell's: ⌘K opens Search (on every page), ⌘J focuses the
 * Ask input, and Escape folds the innermost open thing first: Search or an
 * open More menu (each takes the key itself), the open dock, a wide sheet,
 * then the sheet.
 */
import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import type { AuthSessionResponse } from "@yagni/shared";
import { AuthSessionContext } from "../hooks/useWorkspaceRole.js";
import { cx } from "../queue/primitives.js";
import { AskDock, ASK_INPUT_SELECTOR, focusAskInput } from "./AskDock.js";
import { askDockStore } from "./askDockStore.js";
import { Rail } from "./Rail.js";
import { useRailFolded } from "./railFold.js";
import { SearchPalette } from "./SearchPalette.js";
import { searchStore } from "./searchStore.js";
import { escapeSheet, sheetStore, useSheet } from "./useSheet.js";

/** The workspace surfaces built on the queue grammar, keyed by their first segment. */
const WORKSPACE_SURFACE = /^\/(reviews|decisions|services|settings)(\/|$)/;

/**
 * Whether the Ask dock sits at the foot of this route. Settings is forms
 * (name, members, billing, tokens, sign-on), and the dock never replaces a
 * form, so it is the one signed-in page without it.
 */
export function hasAskDock(pathname: string): boolean {
  return !/^\/settings(\/|$)/.test(pathname);
}

/**
 * A route built on the queue grammar brings its own header and padding (a
 * `.page` column), so the center hands it its full height. Every other
 * signed-in route (Admin, a run's detail, Add tickets) keeps the padded canvas.
 */
export function isQueueRoute(pathname: string): boolean {
  return (
    pathname === "/teams" || // All teams
    pathname === "/waiting" || // Waiting on you
    /^\/desks\/[^/]+$/.test(pathname) || // a Team's queue (and Create a Team at /desks/new)
    /^\/usage(\/|$)/.test(pathname) || // Usage and its drills
    /^\/prototypes\/[^/]+$/.test(pathname) || // one prototype
    WORKSPACE_SURFACE.test(pathname)
  );
}

/**
 * What a sheet belongs to. On Pull request reviews, Decisions, Connections and Settings the sheet
 * belongs to the surface, not the address: `/decisions/:id` is the list with
 * that record open, and moving between records or tabs keeps the surface's
 * sheet in its own hands. Every other page owns its sheet by its path.
 */
export function sheetOwnerOf(pathname: string): string {
  const match = WORKSPACE_SURFACE.exec(pathname);
  return match ? `/${match[1]}` : pathname;
}

/**
 * The shell's keyboard rule, as a pure step over the stores. Returns whether
 * the key was used. ⌘K (Ctrl+K) opens Search wherever it is pressed. Without
 * a dock on the page (Settings), ⌘J is not the shell's: it is left to the
 * browser rather than aimed at an input that is not there.
 */
export function handleShellKey(event: KeyboardEvent, dock = true): boolean {
  const chord = (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey;
  if (event.key === "Escape") {
    if (event.defaultPrevented) return false;
    if (askDockStore.getSnapshot().open) {
      askDockStore.setOpen(false);
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.matches(ASK_INPUT_SELECTOR)) active.blur();
      return true;
    }
    return escapeSheet(sheetStore);
  }
  if (chord && event.key.toLowerCase() === "k") {
    // A surface that took ⌘K for itself keeps it.
    if (event.defaultPrevented) return false;
    if (!searchStore.getSnapshot().open) searchStore.open();
    return true;
  }
  if (chord && event.key.toLowerCase() === "j") {
    return dock && focusAskInput();
  }
  return false;
}

/** The shell's keys on the window: ⌘K, ⌘J (where the dock is) and the Escape fold. */
export function useShellKeys(dock = true): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (handleShellKey(event, dock)) event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dock]);
}

/**
 * A sheet belongs to the page that opened it: a route change closes it. In a
 * layout effect, so it lands before the next page's own effects can open its
 * sheet (a link straight to `/desks/:id?item=` from another page).
 */
export function useSheetFollowsRoute(): void {
  const owner = sheetOwnerOf(useLocation().pathname);
  const shown = useRef(owner);
  // A layout effect, so the old page's sheet is gone before the new page's
  // passive effects open the record its address names (`/decisions/:id`).
  useLayoutEffect(() => {
    if (shown.current === owner) return;
    shown.current = owner;
    sheetStore.close();
  }, [owner]);
}

interface AppShellProps {
  session: AuthSessionResponse;
  onSignOut: () => void;
  onOpenFeedback?: () => void;
  children: ReactNode;
}

export function AppShell({ session, onSignOut, onOpenFeedback, children }: AppShellProps) {
  const { pathname } = useLocation();
  const sheet = useSheet();
  const railFolded = useRailFolded();

  const padded = !isQueueRoute(pathname);
  const dock = hasAskDock(pathname);

  useShellKeys(dock);
  useSheetFollowsRoute();

  return (
    <AuthSessionContext.Provider value={session}>
      <div
        className={cx(
          "app workspace-shell",
          sheet.isOpen && "open",
          ((sheet.isOpen && sheet.wide) || railFolded) && "wide",
        )}
        data-testid="app-shell"
      >
        <Rail session={session} onSignOut={onSignOut} onOpenFeedback={onOpenFeedback} />
        <main className="shell">
          <section className="queue">
            <div className={cx("center", padded && "padded")}>{children}</div>
            {dock && <AskDock />}
          </section>
          <aside className="sheet" aria-label="Record" aria-hidden={!sheet.isOpen}>
            <div className="inner">{sheet.content}</div>
          </aside>
        </main>
        <SearchPalette />
      </div>
    </AuthSessionContext.Provider>
  );
}
